import { Button, Col, Flex, Icon, Row, Text, Tooltip } from '@wyz/ui';
import * as React from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { SelectField } from '~/common/components';
import { Trans, useI18n } from '~/common/context';
import { ICriteriaFormModal } from '~/connect/types';

interface CriteriaVisibilityRulesProps {
  automakerGroupOptions: Array<{ label: string; value: string }>;
}

export const CriteriaVisibilityRules: React.FC<
  CriteriaVisibilityRulesProps
> = ({ automakerGroupOptions }) => {
  const { t } = useI18n();
  const { control } = useFormContext<ICriteriaFormModal>();
  const { fields, append } = useFieldArray({
    control,
    name: 'visibilityRules',
  });
  const values = useWatch({ control, name: 'values' });
  const rules = useWatch({ control, name: 'visibilityRules' });

  const valueOptions = values.map((value) => ({
    label: value.label,
    value: value.uid,
  }));

  // a value belongs to one rule at most
  const isUsedInAnotherRule = (uid: string, ruleIndex: number) =>
    rules.some(
      (rule, index) => index !== ruleIndex && rule.valueUids.includes(uid),
    );

  return (
    <div className='d-flex flex-column gap-3'>
      <Flex alignItems='center' className='gap-2'>
        <Text heading='normal' weight='semi-bold'>
          <Trans i18nKey='app.criteria.form.modal.visibility.title' />
        </Text>
        <Tooltip
          label={<Trans i18nKey='app.criteria.form.modal.visibility.tooltip' />}
        >
          <Icon icon='information-line' width={20} height={20} />
        </Tooltip>
      </Flex>
      {fields.length > 0 && (
        <div
          className='d-flex flex-column gap-2 overflow-x-hidden'
          style={{ maxHeight: '15rem', overflowY: 'auto' }}
        >
          {fields.map((field, index) => (
            <Row key={field.id} className='g-3'>
              <Col xs={6}>
                <SelectField<true>
                  isMulti
                  withCheckboxes
                  maxShown={1}
                  required
                  id={`visibility-rule-values-${index}`}
                  name={`visibilityRules.${index}.valueUids`}
                  label={t('app.criteria.form.modal.visibility.values')}
                  placeholder={t(
                    'app.criteria.form.modal.visibility.placeholder',
                  )}
                  options={valueOptions}
                  isOptionDisabled={(option) =>
                    isUsedInAnotherRule(String(option.value), index)
                  }
                  formGroupClassName='mb-0'
                />
              </Col>
              <Col xs={6}>
                <SelectField<true>
                  isMulti
                  withCheckboxes
                  maxShown={1}
                  required
                  id={`visibility-rule-automaker-groups-${index}`}
                  name={`visibilityRules.${index}.automakerGroupIds`}
                  label={t(
                    'app.criteria.form.modal.visibility.automaker_groups',
                  )}
                  placeholder={t(
                    'app.criteria.form.modal.visibility.placeholder',
                  )}
                  options={automakerGroupOptions}
                  formGroupClassName='mb-0'
                />
              </Col>
            </Row>
          ))}
        </div>
      )}
      <div>
        <Button
          id='add-criteria-visibility-rule'
          type='button'
          variant='tertiary-grey'
          onClick={() => append({ valueUids: [], automakerGroupIds: [] })}
        >
          <Icon icon='plus' width='1.25rem' />{' '}
          <Trans i18nKey='app.criteria.form.modal.visibility.add_rule' />
        </Button>
      </div>
    </div>
  );
};
